import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { 
  Sparkles, 
  ChevronDown, 
  ChevronUp, 
  BookOpen, 
  Plus,
  Check,
  X,
  Edit3,
  Trash2,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { api } from '../../lib/api';
import type { UserStory, WhiteboardElement, AcceptanceCriterion } from '../../types/whiteboard';

interface Props {
  projectId: string;
  userStory: UserStory;
  elements: WhiteboardElement[];
  acceptanceCriteria: AcceptanceCriterion[];
  onUserStoryChange: (story: UserStory) => void;
  onAcceptanceCriteriaChange: (criteria: AcceptanceCriterion[]) => void;
}

export function WhiteboardUserStory({ 
  projectId,
  userStory, 
  elements,
  acceptanceCriteria,
  onUserStoryChange,
  onAcceptanceCriteriaChange,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isACExpanded, setIsACExpanded] = useState(true);
  const [newACText, setNewACText] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  
  // Generate AC mutation
  const generateAC = useMutation({
    mutationFn: async () => {
      // Call your AC generation API
      const response = await api.post('/generate-ac', {
        projectId,
        userStory,
        elements: elements.map(el => ({
          type: el.type,
          label: el.label,
          props: el.props,
        })),
      });
      return response;
    },
    onSuccess: (data: any) => {
      const newCriteria: AcceptanceCriterion[] = (data.criteria || []).map((text: string, i: number) => ({
        id: `ac-${Date.now()}-${i}`,
        text,
        priority: 'must' as const,
        status: 'draft' as const,
        origin: 'ai' as const,
      }));
      onAcceptanceCriteriaChange([...acceptanceCriteria, ...newCriteria]);
    },
  });

  const canGenerate = userStory.asA.trim() && userStory.iWant.trim();

  // Add manual AC
  const handleAddAC = useCallback(() => {
    if (!newACText.trim()) return;
    
    const newAC: AcceptanceCriterion = {
      id: `ac-${Date.now()}`,
      text: newACText.trim(),
      priority: 'must',
      status: 'draft',
      origin: 'manual',
    };
    
    onAcceptanceCriteriaChange([...acceptanceCriteria, newAC]);
    setNewACText('');
  }, [newACText, acceptanceCriteria, onAcceptanceCriteriaChange]);

  // Update AC
  const handleUpdateAC = useCallback((id: string, updates: Partial<AcceptanceCriterion>) => {
    onAcceptanceCriteriaChange(
      acceptanceCriteria.map(ac => ac.id === id ? { ...ac, ...updates } : ac)
    );
  }, [acceptanceCriteria, onAcceptanceCriteriaChange]);

  // Delete AC
  const handleDeleteAC = useCallback((id: string) => {
    onAcceptanceCriteriaChange(acceptanceCriteria.filter(ac => ac.id !== id));
  }, [acceptanceCriteria, onAcceptanceCriteriaChange]);

  // Start editing
  const handleStartEdit = (ac: AcceptanceCriterion) => {
    setEditingId(ac.id);
    setEditText(ac.text);
  };

  // Save edit
  const handleSaveEdit = () => {
    if (editingId && editText.trim()) {
      handleUpdateAC(editingId, { text: editText.trim() });
    }
    setEditingId(null);
    setEditText('');
  };

  // Priority badge colors
  const priorityColors: Record<string, string> = {
    must: 'bg-danger-dim text-danger',
    should: 'bg-warning-dim text-warning',
    could: 'bg-info-dim text-info',
    wont: 'bg-surface-2 text-muted',
  };

  const priorityLabels: Record<string, string> = {
    must: 'Must',
    should: 'Should',
    could: 'Could',
    wont: "Won't",
  };

  return (
    <aside className="w-96 bg-surface border-l border-default flex flex-col overflow-hidden">
      {/* User Story Section */}
      <div className="border-b border-default">
        <div 
          className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-surface-2 transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-default">User Story</h3>
          </div>
          {isExpanded ? <ChevronUp className="w-4 h-4 text-muted" /> : <ChevronDown className="w-4 h-4 text-muted" />}
        </div>
        
        {isExpanded && (
          <div className="px-4 pb-4 space-y-3">
            {/* User Story Fields */}
            <div>
              <label className="text-xs font-medium text-muted block mb-1.5">As a...</label>
              <textarea
                value={userStory.asA}
                onChange={(e) => onUserStoryChange({ ...userStory, asA: e.target.value })}
                placeholder="[role/persona]"
                className="input resize-none h-14 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted block mb-1.5">I want to...</label>
              <textarea
                value={userStory.iWant}
                onChange={(e) => onUserStoryChange({ ...userStory, iWant: e.target.value })}
                placeholder="[action/feature]"
                className="input resize-none h-14 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted block mb-1.5">So that...</label>
              <textarea
                value={userStory.soThat}
                onChange={(e) => onUserStoryChange({ ...userStory, soThat: e.target.value })}
                placeholder="[benefit/value]"
                className="input resize-none h-14 text-sm"
              />
            </div>
            
            {/* Elements summary */}
            <div className="flex items-center justify-between p-3 bg-surface-2 rounded-lg">
              <div>
                <p className="text-xs text-muted">Wireframe Elements</p>
                <p className="text-sm font-semibold text-default">{elements.length} elements</p>
              </div>
              <div className="flex gap-1">
                {elements.slice(0, 3).map(el => (
                  <span key={el.id} className="px-2 py-0.5 text-[10px] bg-surface rounded-full text-muted">
                    {el.type}
                  </span>
                ))}
                {elements.length > 3 && (
                  <span className="px-2 py-0.5 text-[10px] bg-surface rounded-full text-muted">
                    +{elements.length - 3}
                  </span>
                )}
              </div>
            </div>
            
            {/* Generate button */}
            <button
              onClick={() => generateAC.mutate()}
              disabled={generateAC.isPending || !canGenerate}
              className="btn btn-primary w-full"
            >
              {generateAC.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Generate Acceptance Criteria
                </>
              )}
            </button>
            
            {!canGenerate && (
              <p className="text-xs text-muted text-center flex items-center justify-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Fill in "As a" and "I want to" fields
              </p>
            )}
          </div>
        )}
      </div>

      {/* Acceptance Criteria Section */}
      <div className="flex-1 flex flex-col min-h-0">
        <div 
          className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-surface-2 transition-colors border-b border-default"
          onClick={() => setIsACExpanded(!isACExpanded)}
        >
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-success" />
            <h3 className="text-sm font-semibold text-default">Acceptance Criteria</h3>
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-surface-2 rounded-full text-muted">
              {acceptanceCriteria.length}
            </span>
          </div>
          {isACExpanded ? <ChevronUp className="w-4 h-4 text-muted" /> : <ChevronDown className="w-4 h-4 text-muted" />}
        </div>
        
        {isACExpanded && (
          <div className="flex-1 overflow-y-auto">
            {/* AC List */}
            <div className="p-3 space-y-2">
              {acceptanceCriteria.map((ac, index) => (
                <div 
                  key={ac.id}
                  className={`
                    group p-3 rounded-lg border transition-all
                    ${ac.status === 'approved' 
                      ? 'bg-success-dim border-success/30' 
                      : ac.status === 'rejected'
                      ? 'bg-danger-dim border-danger/30'
                      : 'bg-surface-2 border-transparent hover:border-border-strong'
                    }
                  `}
                >
                  {editingId === ac.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="input resize-none h-20 text-sm"
                        autoFocus
                      />
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => setEditingId(null)}
                          className="btn btn-ghost text-xs px-2 py-1"
                        >
                          Cancel
                        </button>
                        <button 
                          onClick={handleSaveEdit}
                          className="btn btn-primary text-xs px-2 py-1"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start gap-2">
                        <span className="text-xs font-medium text-muted mt-0.5">
                          AC{index + 1}
                        </span>
                        <p className="text-sm text-default flex-1">{ac.text}</p>
                      </div>
                      
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-default/50">
                        <div className="flex items-center gap-2">
                          {/* Priority selector */}
                          <select
                            value={ac.priority}
                            onChange={(e) => handleUpdateAC(ac.id, { priority: e.target.value as any })}
                            className={`
                              text-[10px] font-medium px-2 py-0.5 rounded-full border-0 cursor-pointer
                              ${priorityColors[ac.priority]}
                            `}
                          >
                            {Object.entries(priorityLabels).map(([value, label]) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </select>
                          
                          {/* Origin badge */}
                          <span className={`
                            text-[10px] px-1.5 py-0.5 rounded
                            ${ac.origin === 'ai' ? 'bg-primary-dim text-primary' : 'bg-surface text-muted'}
                          `}>
                            {ac.origin === 'ai' ? 'AI' : 'Manual'}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {/* Approve/Reject */}
                          {ac.status === 'draft' && (
                            <>
                              <button
                                onClick={() => handleUpdateAC(ac.id, { status: 'approved' })}
                                className="p-1 rounded hover:bg-success-dim text-muted hover:text-success"
                                title="Approve"
                              >
                                <Check className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleUpdateAC(ac.id, { status: 'rejected' })}
                                className="p-1 rounded hover:bg-danger-dim text-muted hover:text-danger"
                                title="Reject"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                          {ac.status !== 'draft' && (
                            <button
                              onClick={() => handleUpdateAC(ac.id, { status: 'draft' })}
                              className="p-1 rounded hover:bg-surface text-muted"
                              title="Reset to draft"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          
                          {/* Edit */}
                          <button
                            onClick={() => handleStartEdit(ac)}
                            className="p-1 rounded hover:bg-surface text-muted hover:text-default"
                            title="Edit"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          
                          {/* Delete */}
                          <button
                            onClick={() => handleDeleteAC(ac.id)}
                            className="p-1 rounded hover:bg-danger-dim text-muted hover:text-danger"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
              
              {acceptanceCriteria.length === 0 && (
                <div className="text-center py-8">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-surface-2 flex items-center justify-center">
                    <Check className="w-6 h-6 text-muted" />
                  </div>
                  <p className="text-sm text-muted">No acceptance criteria yet</p>
                  <p className="text-xs text-subtle mt-1">Add manually or generate with AI</p>
                </div>
              )}
            </div>
            
            {/* Add manual AC */}
            <div className="p-3 border-t border-default">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newACText}
                  onChange={(e) => setNewACText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddAC()}
                  placeholder="Add acceptance criterion..."
                  className="input flex-1 text-sm"
                />
                <button
                  onClick={handleAddAC}
                  disabled={!newACText.trim()}
                  className="btn btn-secondary px-3"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Summary footer */}
      {acceptanceCriteria.length > 0 && (
        <div className="p-3 border-t border-default bg-surface-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted">Status</span>
            <div className="flex gap-2">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-success" />
                {acceptanceCriteria.filter(ac => ac.status === 'approved').length} approved
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-surface-3" />
                {acceptanceCriteria.filter(ac => ac.status === 'draft').length} draft
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-danger" />
                {acceptanceCriteria.filter(ac => ac.status === 'rejected').length} rejected
              </span>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}

